import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  ledgerPostingTemplateCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { ledgerEndpoints } from "@/features/ledger/api/endpoints";
import type { PostingTemplateDto } from "@/features/ledger/api/dto";

/**
 * Crée un modèle d'imputation en DRAFT (`POST /PostingTemplates/add`) —
 * réservé à flashpay-superadmin / flashpay-admin. Décrit, pour un code
 * produit et un provider, quelles lignes de débit/crédit générer et sur
 * quelle base de montant.
 */
export const useCreatePostingTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["ledger", "posting-templates", "create"],
    mutationFn: (body: PostingTemplateDto) =>
      mutateData({
        url: ledgerEndpoints.postingTemplates.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<PostingTemplateDto>,
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        ledgerPostingTemplateCacheKeys(),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreatePostingTemplate failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "ledger" }
      );
    },
  });
};
